import {
    Button,
    Form,
    type FormProps,
    Input,
    Modal,
    Space,
    type NotificationArgsProps,
    notification,
    Select
} from 'antd'
import React, { useEffect, useState } from 'react'

import { CreatorService } from '@services/CreatorService'
import { type ProductParams, ProductService } from '@services/ProductService'

import { type Creator } from '../../types'
import { slugify } from '../../utils/slugify'

type ModalProps = {
    isOpen: boolean
    onClose: () => void
    refetch: () => Promise<void>
}

type FieldType = {
    name?: string
    slug?: string
    label?: string
    key?: string
    description?: string
    creatorId?: string
}

type NotificationPlacement = NotificationArgsProps['placement']
type NotificationType = 'success' | 'error'

export const CreateProductModal: React.FC<ModalProps> = ({ isOpen, onClose, refetch }) => {
    const [api, contextHolder] = notification.useNotification()
    const [isLoading, setIsLoading] = useState(false)
    const [creators, setCreators] = useState<Creator[]>()
    const [slugTouched, setSlugTouched] = useState(false)
    const [form] = Form.useForm()

    const openNotification = (
        placement: NotificationPlacement,
        type: NotificationType,
        message: string,
        description?: string
    ) => {
        api[type]({
            message,
            description,
            placement
        })
    }

    useEffect(() => {
        const fetchCreators = async () => {
            const creators = await CreatorService.list()
            setCreators(creators)
        }
        fetchCreators()
    }, [])

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value

        // Only auto-generate slug if user hasn't manually edited it
        if (!slugTouched) {
            form.setFieldsValue({
                slug: slugify(title)
            })
        }
    }

    const onFinish: FormProps<FieldType>['onFinish'] = async values => {
        const body: ProductParams = {
            name: values.name!,
            slug: values.slug!,
            label: values.label!,
            key: values.key!,
            description: values.description,
            creatorId: values.creatorId
        }

        try {
            setIsLoading(true)
            await ProductService.create(body)
            openNotification('top', 'success', 'Product created!')
            await refetch()
            onClose()
            form.resetFields()
        } catch (err: any) {
            openNotification('top', 'error', 'Product creation failed!', err.toString())
        } finally {
            setIsLoading(false)
        }
    }

    if (!creators) {
        return null
    }

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            {contextHolder}
            <Form
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                layout="horizontal"
                disabled={false}
                onFinish={onFinish}
                form={form}
            >
                <h3>Create Product</h3>
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter an internal product name' }]}
                >
                    <Input onChange={handleTitleChange} />
                </Form.Item>
                <Form.Item
                    label="Slug"
                    name="slug"
                    rules={[{ required: true, message: 'Please enter an product slug' }]}
                >
                    <Input placeholder="auto generated slug" onChange={() => setSlugTouched(true)} />
                </Form.Item>
                <Form.Item
                    label="Label"
                    name="label"
                    rules={[{ required: true, message: 'Please enter a product label' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item label="Key" name="key" rules={[{ required: true, message: 'Please enter a product key' }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input />
                </Form.Item>
                <Form.Item label="Creator" name="creatorId">
                    <Select
                        style={{ minWidth: '200px' }}
                        options={creators?.map(creator => ({
                            label: creator.name,
                            value: creator.id
                        }))}
                    />
                </Form.Item>
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            Create
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    )
}
